import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

export const AboutPage: React.FC = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `Sobre o ${SITE_NAME}`,
    url: `${SITE_URL}/sobre`,
    inLanguage: 'pt-BR',
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
  };

  return (
    <>
      <Helmet>
        <title>Sobre | {SITE_NAME} — Notícias Cripto em Português</title>
        <meta
          name="description"
          content={`O ${SITE_NAME} é o portal de notícias sobre criptomoedas em português feito para o investidor brasileiro. Curadoria diária das principais fontes do mercado cripto com resumos em PT-BR.`}
        />
        <link rel="canonical" href={`${SITE_URL}/sobre`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-3xl">
          {/* Hero */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl shadow p-10 mb-8 text-white text-center">
            <div className="text-5xl mb-4">₿</div>
            <h1 className="text-4xl font-bold mb-3">{SITE_NAME}</h1>
            <p className="text-blue-100 text-lg">
              O mercado cripto em português, feito para o investidor brasileiro.
            </p>
          </div>

          {/* Nossa Missão */}
          <div className="bg-white rounded-xl shadow p-8 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Nossa Missão</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              O <strong>{SITE_NAME}</strong> nasceu de uma necessidade real: o mercado de criptomoedas é
              predominantemente em inglês, mas o Brasil é um dos países com maior adoção de criptoativos
              do mundo. Segundo a Receita Federal, mais de 1,4 milhão de brasileiros declararam criptoativos
              no Imposto de Renda — e esse número cresce a cada ano.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Nossa missão é simples: <strong>trazer as principais notícias do mundo cripto em português,
              com contexto relevante para o leitor brasileiro</strong>. Fazemos a curadoria e a tradução do
              conteúdo das mais respeitadas publicações internacionais de criptomoedas, para que você não
              precise perder tempo vasculhando dezenas de sites em inglês.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Cada artigo publicado aqui passa por nosso sistema de curadoria e ganha um <strong>resumo
              em português</strong> que destaca os pontos mais relevantes para o mercado brasileiro —
              considerando regulação local, impacto no real, e contexto do investidor nacional.
            </p>
          </div>

          {/* Por que em português? */}
          <div className="bg-white rounded-xl shadow p-8 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Por que notícias cripto em português?</h2>
            <div className="space-y-4 text-gray-700">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-lg">🇧🇷</div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Brasil entre os maiores mercados cripto</h3>
                  <p className="text-sm leading-relaxed">O Brasil é consistentemente um dos 10 maiores mercados de criptomoedas do mundo. Em 2023, exchanges no Brasil movimentaram mais de R$ 100 bilhões em transações.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-lg">📰</div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Conteúdo 90% em inglês</h3>
                  <p className="text-sm leading-relaxed">As principais publicações cripto — CoinDesk, CoinTelegraph, Bitcoin.com — publicam em inglês. A barreira linguística deixa milhões de brasileiros sem acesso ao melhor conteúdo do setor.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center text-lg">⚡</div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Velocidade importa no cripto</h3>
                  <p className="text-sm leading-relaxed">Notícias sobre regulação, hacks, novos projetos e movimentos de mercado podem impactar o preço de ativos em minutos. Acompanhar em tempo real, em português, faz diferença.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Como funciona */}
          <div className="bg-white rounded-xl shadow p-8 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Como Funciona a Curadoria</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-3">1</div>
                <h3 className="font-semibold text-gray-900 mb-2">Monitoramento Automático</h3>
                <p className="text-gray-600 text-sm">
                  Nosso sistema monitora as principais fontes cripto a cada 30 minutos, 24h por dia, 7 dias por semana — incluindo CoinDesk, CoinTelegraph, Bitcoin.com e CryptoSlate.
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-3">2</div>
                <h3 className="font-semibold text-gray-900 mb-2">Resumo em Português</h3>
                <p className="text-gray-600 text-sm">
                  Cada artigo recebe um resumo em português com os pontos principais, contextualizados para o leitor brasileiro. O conteúdo original em inglês também fica disponível.
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-3">3</div>
                <h3 className="font-semibold text-gray-900 mb-2">Categorização e Tags</h3>
                <p className="text-gray-600 text-sm">
                  Artigos são automaticamente categorizados por tema — Bitcoin, Ethereum, DeFi, Regulação, NFT — facilitando encontrar o que é relevante para você.
                </p>
              </div>
            </div>
          </div>

          {/* Fontes */}
          <div className="bg-white rounded-xl shadow p-8 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Nossas Fontes</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Agregamos conteúdo exclusivamente de publicações estabelecidas e reconhecidas no mercado cripto internacional. Todos os artigos linkam para a fonte original, permitindo que você leia a história completa sempre que quiser.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: 'CoinDesk', desc: 'Fundada em 2013. Referência global em jornalismo cripto.', color: 'blue' },
                { name: 'CoinTelegraph', desc: 'Publicação global de blockchain e criptomoedas desde 2013.', color: 'green' },
                { name: 'Bitcoin.com', desc: 'Notícias e recursos sobre o ecossistema Bitcoin.', color: 'orange' },
                { name: 'CryptoSlate', desc: 'Notícias, dados e pesquisa sobre criptomoedas.', color: 'purple' },
              ].map((source) => (
                <div key={source.name} className="bg-gray-50 rounded-xl p-4 text-center">
                  <h3 className="font-bold text-gray-900 mb-1 text-sm">{source.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{source.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Como usamos IA */}
          <div className="bg-white rounded-xl shadow p-8 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Como Usamos Inteligência Artificial</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Usamos IA como ferramenta de apoio editorial — nunca como substituta da responsabilidade
              humana. Hoje, a IA nos ajuda em três tarefas: <strong>tradução</strong> de títulos e textos
              para o português, <strong>resumos</strong> dos pontos principais de cada notícia e{' '}
              <strong>classificação</strong> de temas.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Todo conteúdo produzido com auxílio de IA é identificado no próprio artigo e passa por
              validações automáticas (filtro editorial, checagem de fonte e de duplicidade) e revisão
              humana por amostragem. Erros podem ser reportados por qualquer leitor — e são corrigidos
              conforme nossa <Link to="/politica-de-correcoes" className="text-blue-600 hover:underline">Política de Correções</Link>.
            </p>
            <div className="flex flex-wrap gap-3 text-sm">
              <Link to="/politica-editorial" className="bg-blue-50 text-blue-700 font-semibold px-4 py-2 rounded-lg hover:bg-blue-100 transition">
                📋 Política Editorial
              </Link>
              <Link to="/politica-de-correcoes" className="bg-blue-50 text-blue-700 font-semibold px-4 py-2 rounded-lg hover:bg-blue-100 transition">
                ✏️ Política de Correções
              </Link>
              <Link to="/autores" className="bg-blue-50 text-blue-700 font-semibold px-4 py-2 rounded-lg hover:bg-blue-100 transition">
                👥 Autores e Equipe
              </Link>
            </div>
          </div>

          {/* Transparência e Disclaimer */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
            <h2 className="text-lg font-bold text-yellow-800 mb-3">Transparência Editorial</h2>
            <div className="space-y-2 text-yellow-800 text-sm leading-relaxed">
              <p>
                O <strong>{SITE_NAME}</strong> é um serviço de curadoria e agregação de notícias. Não produzimos o conteúdo original dos artigos — nosso valor está na curadoria, tradução dos destaques e contextualização para o leitor brasileiro.
              </p>
              <p>
                <strong>Não fornecemos aconselhamento financeiro ou de investimento.</strong> O conteúdo publicado é informativo e não deve ser interpretado como recomendação de compra, venda ou manutenção de qualquer criptoativo. Sempre consulte um profissional qualificado antes de tomar decisões financeiras.
              </p>
              <p>
                Investimentos em criptomoedas envolvem alto risco e volatilidade. O valor dos ativos pode cair a zero. Nunca invista mais do que pode perder.
              </p>
            </div>
          </div>

          {/* Recursos */}
          <div className="bg-white rounded-xl shadow p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Recursos do {SITE_NAME}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link to="/glossario" className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-blue-200 hover:bg-blue-50 transition">
                <span className="text-2xl">📚</span>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Glossário Cripto</div>
                  <div className="text-gray-500 text-xs">25+ termos explicados em português</div>
                </div>
              </Link>
              <Link to="/arbitrage" className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-blue-200 hover:bg-blue-50 transition">
                <span className="text-2xl">⚡</span>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Monitor de Arbitragem</div>
                  <div className="text-gray-500 text-xs">Diferenças de preço entre exchanges em tempo real</div>
                </div>
              </Link>
              <Link to="/trending" className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-blue-200 hover:bg-blue-50 transition">
                <span className="text-2xl">🔥</span>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Em Alta</div>
                  <div className="text-gray-500 text-xs">As notícias mais lidas do momento</div>
                </div>
              </Link>
              <Link to="/contact" className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-blue-200 hover:bg-blue-50 transition">
                <span className="text-2xl">✉️</span>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Fale Conosco</div>
                  <div className="text-gray-500 text-xs">Dúvidas, sugestões e parcerias</div>
                </div>
              </Link>
            </div>
          </div>

          {/* Responsável e Identificação */}
          <div className="bg-white rounded-xl shadow p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Responsável pelo Site</h2>
            <p className="text-gray-700 mb-4 leading-relaxed text-sm">
              O {SITE_NAME} é operado por pessoa física, comprometida com a transparência e com a conformidade
              com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018).
            </p>
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 space-y-1">
              <p><strong>Responsável:</strong> Gabriel Leite Moreira</p>
              <p><strong>Localização:</strong> Blumenau, SC, Brasil</p>
              <p><strong>E-mail:</strong> <a href="mailto:contact@criptonews.site" className="text-blue-600 hover:underline">contact@criptonews.site</a></p>
              <p><strong>Site:</strong> <a href={SITE_URL} className="text-blue-600 hover:underline">{SITE_URL}</a></p>
            </div>
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <a href="/politica-de-privacidade" className="text-blue-600 hover:underline">Política de Privacidade</a>
              <span className="text-gray-300">|</span>
              <a href="/termos-de-uso" className="text-blue-600 hover:underline">Termos de Uso</a>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-8 text-center text-white">
            <h2 className="text-xl font-bold mb-3">Tem alguma dúvida ou sugestão?</h2>
            <p className="text-blue-100 mb-5 text-sm">
              Estamos sempre melhorando. Entre em contato — respondemos em até 2 dias úteis.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-white text-blue-700 font-semibold px-8 py-3 rounded-xl hover:bg-blue-50 transition"
            >
              Entrar em Contato
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
